import { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link as RouterLink } from 'react-router';
import TextField from '@mui/material/TextField';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import { useLoginMutation } from '#services/auth';
import useSetState from '#utils/use-set-state';
import Link from '@mui/material/Link';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import { Divider } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { SxProps, Theme } from '@mui/material/styles';
import type { CSSObject } from '@mui/system';
import { Helmet } from 'react-helmet-async';
import { revertAll } from '../actions';
import type { AppConfigState } from '../app/config.slice';

interface LoginFormState {
  email: string;
  password: string;
}

interface LoginValidateResult {
  email: string | null;
  password: string | null;
}

const initialState: LoginFormState = { email: '', password: '' };
const initialValidateResult: LoginValidateResult = { email: null, password: null };

const validateLoginData = (data: LoginFormState): { email: string | null } => {
  const { email } = data;
  const errors: { email: string | null } = { email: null };

  // validate email
  if (email && !/^\S+@\S+$/.test(email)) {
    errors.email = 'Invalid e-mail address';
  }

  return errors;
};

const buildAlertElements = (error?: string | null) => {
  if (!error) {
    return null;
  }

  return (
    <Stack sx={{ width: '100%', mt: 2 }} spacing={2}>
      <Alert severity="error">{error}</Alert>
    </Stack>
  );
};

export default function Login() {
  const [state, setState] = useSetState(initialState);
  const appConfig = useSelector((s: { appConfig: AppConfigState }) => s.appConfig);

  const validateErrors = useMemo(
    () => ({ ...initialValidateResult, ...validateLoginData(state) }),
    [state],
  );
  const dispatch = useDispatch();

  const loginError = useSelector((s: { auth: { loginError?: string } }) => s.auth.loginError);
  const isLoginPending = useSelector((s: { auth: { isLoginPending?: boolean } }) => s.auth.isLoginPending);
  const matchMobileWidth = useMediaQuery('(min-width: 450px)', { noSsr: true });
  const [showFullWitdh, setShowFullWitdh] = useState(true);

  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  // Update state based on media query changes
  useEffect(() => {
    setShowFullWitdh(matchMobileWidth);
  }, [matchMobileWidth]);

  const [doLogin] = useLoginMutation();

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    dispatch(revertAll());

    void doLogin({
      email: state.email,
      password: state.password,
    }).then((res) => {
      if (res.error) {
        return;
      }

      // force page reload to get the latest assets
      window.location.href = '/';
    });
  };

  let loginFormPosition: CSSObject = { left: 'calc(50% - 220px)', top: '10%' };
  switch (appConfig.loginFormPosition) {
    case 'top, left':
      loginFormPosition = { left: '30px', top: '30px' };
      break;
    case 'top, right':
      loginFormPosition = { right: '30px', top: '30px' };
      break;
    case 'bottom, left':
      loginFormPosition = { left: '30px', bottom: '30px' };
      break;
    case 'bottom, right':
      loginFormPosition = { right: '30px', bottom: '30px' };
      break;
    default:
      break;
  }

  const loginBgSx: SxProps<Theme> | undefined = appConfig.loginBgImage
    ? {
      backgroundImage: `url("/public/static/images/${appConfig.loginBgImage}")`,
      backgroundSize: 'cover',
      maxWidth: 'unset !important',
      padding: '0 !important',
      height: '100%',
    }
    : undefined;

  const mobileLoginFormStyle: CSSObject = {
    position: 'relative',
    margin: 'auto',
    left: 'unset',
    top: '30px',
  };

  const desktopLoginFormStyle: CSSObject = {
    position: 'absolute',
    ...loginFormPosition,
  };

  return (
    <>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>
      <Container
        component="main"
        sx={loginBgSx}
      >
        <Box sx={{
          maxWidth: 345,
          ...(showFullWitdh ? desktopLoginFormStyle : mobileLoginFormStyle),
        }}
        >
          <Card component="form" onSubmit={onSubmit} noValidate>
            {!appConfig.loginFormHideLogo && (
            <CardMedia
              component="img"
              alt={appConfig.wholesaleName ? `${appConfig.wholesaleName} logo` : 'Logo'}
              height="auto"
              width="360px"
              object-fit="contain"
              image={`/public/static/images/${appConfig.wholesaleLogo ? appConfig.wholesaleLogo : 'default-logo.png'}`}
            />
            )}
            <CardContent>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                {buildAlertElements(loginError)}
              </Typography>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                value={state.email}
                error={!!validateErrors.email}
                helperText={validateErrors.email}
                onChange={(e) => setState({ email: e.target.value })}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="password"
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={state.password}
                onChange={(e) => setState({ password: e.target.value })}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>,
                  },
                }}
              />
            </CardContent>
            <CardActions sx={{ display: 'block' }}>
              <Button
                type="submit"
                size="small"
                sx={{ ml: '5px', mt: '-10px' }}
                disabled={
                  !state.email
                  || !state.password
                  || !!validateErrors.email
                  || !!validateErrors.password
                }
                loading={isLoginPending}
              >
                Sign In
              </Button>
              <Link
                component={RouterLink}
                to="/request-password-reset"
                underline="none"
                sx={{ float: 'right', mr: '8px', mt: '-6px' }}
              >
                Forgot Password?
              </Link>
              <Divider sx={{ mt: 1 }}>
                {' '}
                <Typography variant="caption" gutterBottom sx={{
                  display: "block"
                }}>
                  <Link
                    underline="none"
                    href="https://smallwm.com"
                    sx={{
                      color: "#4c505d",
                      fontStyle: 'italic'
                    }}>
                    Powered by &#169;
                    {' '}
                    {new Date().getFullYear()}
                    {' '}
                    SWM Platform
                  </Link>
                </Typography>
              </Divider>
            </CardActions>
          </Card>
        </Box>
      </Container>
    </>
  );
}
